import * as React from 'react';
import { useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Phone, MapPin, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { AutoTextarea } from './ui/auto-textarea';
import { Label } from './ui/label';
import { toast } from '@/hooks/use-toast';
import {
  ContactFormData,
  ValidationErrors,
  SubmissionState,
  validateForm,
  saveDraft,
  loadDraft,
  clearDraft,
  isSubmissionThrottled,
  setSubmissionTimestamp,
  getThrottleTimeRemaining,
  checkHoneypot,
  createSubmissionPayload,
} from '@/utils/contactFormUtils';
import { supabase } from '@/integrations/supabase/client';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { getGridItemVariants } from '@/lib/animations';
import { useParallaxRef } from '@/utils/parallax';

export default function EnhancedContact() {
  const [formData, setFormData] = React.useState<ContactFormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
    honeypot: '',
  });
  const [errors, setErrors] = React.useState<ValidationErrors>({});
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});
  const [submissionState, setSubmissionState] = React.useState<SubmissionState>({
    status: 'idle',
  });
  const [throttleTimeLeft, setThrottleTimeLeft] = React.useState(0);
  const [draftRestored, setDraftRestored] = React.useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);
  const successAnnouncerRef = React.useRef<HTMLDivElement>(null);
  const errorAnnouncerRef = React.useRef<HTMLDivElement>(null);

  // Load draft on mount
  React.useEffect(() => {
    const draft = loadDraft();
    if (draft && !draftRestored) {
      setFormData((prev) => ({
        ...prev,
        ...draft,
      }));
      setDraftRestored(true);
    }
  }, [draftRestored]);

  // Auto-save draft
  React.useEffect(() => {
    if (draftRestored && (formData.name || formData.email || formData.message)) {
      saveDraft(formData);
    }
  }, [formData, draftRestored]);

  // Throttle timer
  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (throttleTimeLeft > 0) {
      interval = setInterval(() => {
        const remaining = getThrottleTimeRemaining();
        setThrottleTimeLeft(remaining);
        if (remaining === 0) {
          setSubmissionState({
            status: 'idle',
          });
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [throttleTimeLeft]);
  const handleInputChange = (field: keyof ContactFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear field error when user starts typing
    if (errors[field as keyof ValidationErrors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };
  const handleBlur = (field: keyof ContactFormData) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));

    // Validate single field on blur
    const fieldErrors = validateForm(formData);
    if (fieldErrors[field as keyof ValidationErrors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: fieldErrors[field as keyof ValidationErrors],
      }));
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check throttle
    if (isSubmissionThrottled()) {
      const remaining = getThrottleTimeRemaining();
      setThrottleTimeLeft(remaining);
      setSubmissionState({
        status: 'throttled',
        message: `Please wait ${remaining} seconds before submitting again.`,
      });
      return;
    }

    // Check honeypot (anti-spam)
    if (checkHoneypot(formData.honeypot || '')) {
      // Fake success for spam bots
      setSubmissionState({
        status: 'success',
      });
      setTimeout(
        () =>
          setSubmissionState({
            status: 'idle',
          }),
        5000,
      );
      return;
    }

    // Validate form
    const formErrors = validateForm(formData);
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setTouched({
        name: true,
        email: true,
        subject: true,
        message: true,
      });
      // Focus first error field
      const firstErrorField = Object.keys(formErrors)[0];
      const element = formRef.current?.querySelector(`[name="${firstErrorField}"]`) as HTMLElement;
      element?.focus();
      return;
    }
    setSubmissionState({
      status: 'submitting',
    });
    try {
      // Call the Supabase edge function
      const { data, error } = await supabase.functions.invoke('contact-submit', {
        body: {
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        },
      });

      if (error) {
        console.error('[contact-form] Edge function error:', error);
        setSubmissionState({
          status: 'error',
          message: error.message || 'Failed to send message. Please check your connection and try again.',
        });
        toast({
          title: 'Failed to send message',
          description: error.message || 'Please check your connection and try again.',
          variant: 'destructive',
        });
        return;
      }

      // Check response structure
      if (!data?.ok) {
        if (data?.code === 'VALIDATION_ERROR' && data?.fields) {
          // Server validation errors
          setErrors((prev) => ({ ...prev, ...data.fields }));
          setSubmissionState({
            status: 'error',
            message: 'Please correct the highlighted fields.',
          });
          toast({
            title: 'Check your input',
            description: 'Please correct the highlighted fields.',
            variant: 'destructive',
          });
          return;
        }

        if (data?.code === 'RATE_LIMIT') {
          setSubmissionState({ status: 'error', message: 'Too many attempts, please wait.' });
          toast({
            title: 'Rate limited',
            description: 'Too many attempts, please wait.',
            variant: 'destructive',
          });
          return;
        }

        if (data?.code === 'CONFIG_ERROR') {
          setSubmissionState({ 
            status: 'error', 
            message: 'Email service is temporarily unavailable. Please try again later or contact me directly.' 
          });
          toast({
            title: 'Service unavailable',
            description: 'Email service is temporarily unavailable. Please try again later.',
            variant: 'destructive',
          });
          return;
        }

        if (data?.code === 'MAIL_SEND_FAILED') {
          setSubmissionState({ 
            status: 'error', 
            message: 'Failed to send email. Please try again or contact me directly.' 
          });
          toast({
            title: 'Failed to send message',
            description: 'Please try again or contact me directly.',
            variant: 'destructive',
          });
          return;
        }

        // Generic error
        setSubmissionState({
          status: 'error',
          message: data?.message || 'Failed to send message. Please try again.',
        });
        toast({
          title: 'Failed to send message',
          description: data?.message || 'Please try again.',
          variant: 'destructive',
        });
        return;
      }

      // Success
      setSubmissionState({ status: 'success' });
      setSubmissionTimestamp();
      clearDraft();
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        honeypot: '',
      });
      setErrors({});
      setTouched({});
      toast({
        title: 'Message sent successfully!',
        description: "Thanks! I got your message. I'll reply soon.",
      });
      if (successAnnouncerRef.current) {
        successAnnouncerRef.current.textContent = "Message sent successfully! I'll reply soon.";
      }
      setTimeout(() => setSubmissionState({ status: 'idle' }), 3000);
    } catch (error) {
      console.error('[contact-form] Unexpected error:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Network error. Please try again.';
      setSubmissionState({
        status: 'error',
        message: errorMessage,
      });
      toast({
        title: 'Failed to send message',
        description: errorMessage,
        variant: 'destructive',
      });

      // Announce error for screen readers
      if (errorAnnouncerRef.current) {
        errorAnnouncerRef.current.textContent = `Error: ${errorMessage}`;
      }
      setTimeout(
        () =>
          setSubmissionState({
            status: 'idle',
          }),
        5000,
      );
    }
  };
  const isFormValid = Object.keys(validateForm(formData)).length === 0;

  // Use reveal once hooks to prevent cards from disappearing after scroll
  const faqLinkReveal = useRevealOnce({ amount: 0.3 });
  const businessHoursReveal = useRevealOnce({ amount: 0.3 });
  const contactFormReveal = useRevealOnce({ amount: 0.3 });

  // Parallax refs (matching Info page)
  const heroSubRef = useParallaxRef({ speed: -0.18, axis: 'y', maxTranslate: 64 });

  // Custom animation variants for side-by-side cards
  const leftCardVariants = {
    hidden: { opacity: 0, x: -100 },
    show: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
    },
  };

  const rightCardVariants = {
    hidden: { opacity: 0, x: 100 },
    show: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
    },
  };

  // Subtle randomized, layered gradient background (same as Home)
  const backgroundStyle = useMemo<React.CSSProperties>(() => {
    const rand = (min: number, max: number) => Math.random() * (max - min) + min;

    const hue = rand(205, 220);
    const saturation = rand(65, 78);
    const lightNearWhite = rand(96, 98);
    const lightBlue = rand(88, 92);
    const alphaSoft1 = rand(0.06, 0.1);
    const alphaSoft2 = rand(0.04, 0.08);

    const centerX = rand(30, 70);
    const centerY = rand(15, 45);

    const verticalAngle = 180;
    const horizontalAngle = 90;

    const color1 = `hsla(${hue}, ${saturation}%, ${lightNearWhite}%, 1)`;
    const color2 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, 1)`;
    const tint1 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, ${alphaSoft1})`;
    const tint2 = `hsla(${hue}, ${saturation}%, ${lightNearWhite}%, ${alphaSoft2})`;

    const backgroundImage = [
      `radial-gradient(ellipse at ${centerX}% ${centerY}%, ${tint1} 0%, transparent 60%)`,
      `linear-gradient(${verticalAngle}deg, #ffffff 0%, ${color2} 100%)`,
      `linear-gradient(${horizontalAngle}deg, ${tint2} 0%, transparent 50%, ${tint2} 100%)`,
    ].join(', ');

    return {
      backgroundImage,
      backgroundColor: '#ffffff',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover, cover, cover',
      backgroundAttachment: 'scroll',
    };
  }, []);

  return (
    <div className="min-h-screen py-20" style={backgroundStyle}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 stagger-fade-1">
          <h1 className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg">
            Contact Me
          </h1>
          <p ref={heroSubRef as any} className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed font-normal">
            How can I help you?
          </p>
        </div>

        {/* FAQ Quick Links and Business Hours - Side by Side Cards */}
        <div className="flex justify-center mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl w-full">
            {/* FAQ Quick Links - Left Card */}
            <motion.div
              ref={faqLinkReveal.ref}
              className="bg-card border border-border rounded-3xl p-7 text-center aspect-square flex flex-col justify-center shadow-sm"
              variants={leftCardVariants}
              initial="hidden"
              animate={faqLinkReveal.controls}
            >
              <h3 className="text-2xl font-bold text-card-foreground mb-4">Have a question?</h3>
              <p className="text-muted-foreground mb-6 whitespace-nowrap text-center">
                Get your answer now on the FAQs page
              </p>
              <div className="flex justify-center">
                <a
                  href="/faqs"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl font-poppins tracking-wide focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0000FF] relative overflow-hidden text-[#B3D9FF] font-semibold bg-[linear-gradient(135deg,#0000FF_0%,#4D7DFF_50%,#00B2FF_100%)] shadow-[0_2px_8px_rgba(0,0,255,0.2),0_1px_3px_rgba(0,0,255,0.1)] transition-[color,background] duration-200 ease-out hover:bg-[linear-gradient(135deg,#0000FF_0%,#000000_100%)] hover:text-white hover:shadow-[0_4px_12px_rgba(0,0,255,0.25),0_2px_6px_rgba(0,0,0,0.15)] active:bg-[linear-gradient(135deg,#0000CC_0%,#000000_100%)] active:text-white motion-reduce:hover:bg-[linear-gradient(135deg,#0000FF_0%,#000000_100%)] motion-reduce:hover:text-white before:content-[''] before:absolute before:inset-0 before:rounded-2xl before:bg-[linear-gradient(180deg,rgba(255,255,255,0.22),rgba(255,255,255,0.06))] before:opacity-[0.35] before:pointer-events-none motion-safe:hover:before:translate-x-[12px] motion-safe:hover:before:translate-y-[-6px] motion-reduce:hover:before:translate-x-0 motion-reduce:hover:before:translate-y-0 before:transition-transform before:duration-[200ms] before:ease-out px-6 min-h-[52px] text-lg"
                >
                  FAQs
                </a>
              </div>
            </motion.div>

            {/* Business Hours - Right Card */}
            <motion.div
              ref={businessHoursReveal.ref}
              className="bg-card border border-border rounded-3xl p-7 aspect-square flex flex-col justify-center shadow-sm"
              variants={rightCardVariants}
              initial="hidden"
              animate={businessHoursReveal.controls}
            >
              <h3 className="text-2xl font-bold text-card-foreground mb-6 text-center">Business Hours</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-baseline">
                  <span className="text-muted-foreground whitespace-nowrap">Monday - Friday</span>
                  <span className="text-card-foreground font-medium whitespace-nowrap">9am-5pm EST</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-muted-foreground whitespace-nowrap">Saturday</span>
                  <span className="text-card-foreground font-medium whitespace-nowrap">9am-1pm EST</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-muted-foreground whitespace-nowrap">Sunday</span>
                  <span className="text-card-foreground font-medium whitespace-nowrap">Closed</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Enhanced Contact Form */}
        <motion.div
          ref={contactFormReveal.ref}
          className="bg-card border border-border rounded-3xl p-8 shadow-lg mb-12"
          variants={getGridItemVariants(2)}
          initial="hidden"
          animate={contactFormReveal.controls}
        >
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-card-foreground mt-6 mb-16 text-center">Send me a message</h2>
            </div>

            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" noValidate>
              {/* Honeypot field (hidden) */}
              <input
                type="text"
                name="website"
                value={formData.honeypot || ''}
                onChange={(e) => handleInputChange('honeypot', e.target.value)}
                style={{
                  display: 'none',
                }}
                tabIndex={-1}
                autoComplete="off"
              />

              {/* Name and Email Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium text-card-foreground">
                    Your Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    onBlur={() => handleBlur('name')}
                    placeholder="John Doe"
                    className={`${errors.name ? 'border-destructive' : ''}`}
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? 'name-error' : undefined}
                  />
                  {errors.name && (
                    <p id="name-error" className="text-sm text-destructive flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.name}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-card-foreground">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    onBlur={() => handleBlur('email')}
                    placeholder="john@example.com"
                    className={`${errors.email ? 'border-destructive' : ''}`}
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                  />
                  {errors.email && (
                    <p
                      id="email-error"
                      className="text-sm text-destructive flex items-center gap-1"
                    >
                      <AlertCircle className="w-4 h-4" />
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>

              {/* Subject Input */}
              <div className="space-y-2">
                <Label htmlFor="subject" className="text-sm font-medium text-card-foreground">
                  Subject *
                </Label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  onBlur={() => handleBlur('subject')}
                  placeholder="e.g., Domain purchase question"
                  className={`${errors.subject ? 'border-destructive' : ''}`}
                  aria-invalid={!!errors.subject}
                  aria-describedby={errors.subject ? 'subject-error' : undefined}
                />
                {errors.subject && (
                  <p
                    id="subject-error"
                    className="text-sm text-destructive flex items-center gap-1"
                  >
                    <AlertCircle className="w-4 h-4" />
                    {errors.subject}
                  </p>
                )}
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="text-sm font-medium text-card-foreground">
                  Message *
                </Label>
                <AutoTextarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  maxLength={5000}
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  onBlur={() => handleBlur('message')}
                  placeholder="Tell me more about what you're looking for..."
                  className={`${errors.message ? 'border-destructive' : ''} pb-8`}
                  aria-invalid={!!errors.message}
                  aria-describedby={errors.message ? 'message-error' : undefined}
                />
                {errors.message && (
                  <p
                    id="message-error"
                    className="text-sm text-destructive flex items-center gap-1"
                  >
                    <AlertCircle className="w-4 h-4" />
                    {errors.message}
                  </p>
                )}
              </div>

              {/* Status Messages */}
              {submissionState.status === 'throttled' && (
                <div className="flex items-center gap-2 p-4 bg-muted border border-border rounded-lg text-muted-foreground">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">
                    Please wait {throttleTimeLeft} seconds before submitting again.
                  </p>
                </div>
              )}

              {submissionState.status === 'error' && submissionState.message && (
                <div className="flex items-center gap-2 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">{submissionState.message}</p>
                </div>
              )}

              {/* Form Actions */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button
                  type="submit"
                  disabled={
                    submissionState.status === 'submitting' ||
                    submissionState.status === 'success' ||
                    !isFormValid ||
                    submissionState.status === 'throttled'
                  }
                  className="flex-1 bg-gradient-to-r from-attain-orange to-attain-orange-dark hover:from-attain-orange-dark hover:to-attain-orange text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:scale-100 disabled:opacity-50"
                >
                  {submissionState.status === 'success' ? (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Message Sent!
                    </>
                  ) : submissionState.status === 'submitting' ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : submissionState.status === 'throttled' ? (
                    <>
                      <AlertCircle className="w-5 h-5 mr-2" />
                      Wait {throttleTimeLeft}s
                    </>
                  ) : (
                    <>
                      Send
                      <Send className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </div>

              {/* Help Text */}
            </form>

            {/* Screen reader announcements */}
            <div
              ref={successAnnouncerRef}
              aria-live="polite"
              aria-atomic="true"
              className="sr-only"
            />
            <div
              ref={errorAnnouncerRef}
              aria-live="assertive"
              aria-atomic="true"
              className="sr-only"
            />
        </motion.div>
      </div>
    </div>
  );
}
